import React from 'react';
import {
  BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';

import ChartCard from '../components/ChartCard';
import LoadingSpinner from '../components/LoadingSpinner';
import { useAnalyticsData } from '../hooks/useAnalyticsData';
import { PeriodConfig, formatNumber, tooltipStyle } from '../types';

interface UserSegmentsTabProps {
  period: PeriodConfig;
}

const ENDPOINTS = ['user-segments'];

const SEGMENT_LABELS: Record<string, string> = {
  feed_only: 'Feed Only', creator: 'Creator', ai_lens: 'AI Lens',
  health_tracker: 'Health Tracker', kundli: 'Palm Kundli', im_power: 'IM Power User',
  leaderboard: 'Leaderboard', multi_feature: 'Multi-Feature', casual: 'Casual', ai_chat: 'Ask AI',
};

const UserSegmentsTab: React.FC<UserSegmentsTabProps> = ({ period }) => {
  const { data, loading } = useAnalyticsData(ENDPOINTS, period);

  if (loading) return <LoadingSpinner />;

  const seg = data['user-segments'] || {};
  const segmentStats: any[] = seg.segmentStats || [];
  const combos: any[] = seg.featureCombinations || [];
  const powerUsers: any[] = seg.powerUsers || [];
  const correlations: any[] = seg.retentionCorrelations || [];
  const meta = seg.meta || {};

  if (!seg.segmentStats) {
    return (
      <div className="space-y-6">
        <div className="p-8 bg-white/5 rounded-xl text-center">
          <p className="text-gray-400 text-lg mb-2">No segment analysis data yet</p>
          <p className="text-gray-500 text-sm">Run the segment analysis job to generate data:</p>
          <code className="text-xs text-gray-500 mt-2 block">kubectl apply -f server/k8s/segment-analysis-job.yaml</code>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Meta */}
      <div className="flex items-center gap-4 text-xs text-gray-500">
        <span>Users analyzed: {formatNumber(meta.totalUsersAnalyzed || 0)}</span>
        <span>Window: {meta.analysisWindow}</span>
        <span>Generated: {meta.generatedAt ? new Date(meta.generatedAt).toLocaleString() : '—'}</span>
      </div>

      {/* Segment Comparison Table */}
      <ChartCard title="User Segment Comparison (30 days)">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-gray-400 border-b border-white/10">
                <th className="text-left py-2 px-3">Segment</th>
                <th className="text-right py-2 px-3">Users</th>
                <th className="text-right py-2 px-3">Avg Days</th>
                <th className="text-right py-2 px-3">Avg Sessions</th>
                <th className="text-right py-2 px-3">Avg Duration</th>
                <th className="text-right py-2 px-3">D1 Ret</th>
                <th className="text-right py-2 px-3">D7 Ret</th>
                <th className="text-right py-2 px-3">D30 Ret</th>
              </tr>
            </thead>
            <tbody>
              {segmentStats.map((s: any) => (
                <tr key={s.segment} className="border-b border-white/5 hover:bg-white/5">
                  <td className="py-2 px-3 text-white font-medium">{SEGMENT_LABELS[s.segment] || s.segment}</td>
                  <td className="py-2 px-3 text-right text-gray-300">{formatNumber(s.userCount)}</td>
                  <td className="py-2 px-3 text-right text-gray-300">{s.avgDaysActive}</td>
                  <td className="py-2 px-3 text-right text-gray-300">{s.avgSessionsPerUser}</td>
                  <td className="py-2 px-3 text-right text-gray-300">{s.avgSessionDurationMin > 0 ? `${s.avgSessionDurationMin}m` : '—'}</td>
                  <td className="py-2 px-3 text-right">
                    <span className={s.retentionD1 > 30 ? 'text-green-400' : s.retentionD1 > 15 ? 'text-yellow-400' : 'text-red-400'}>{s.retentionD1}%</span>
                  </td>
                  <td className="py-2 px-3 text-right">
                    <span className={`font-bold ${s.retentionD7 > 20 ? 'text-green-400' : s.retentionD7 > 10 ? 'text-yellow-400' : 'text-red-400'}`}>{s.retentionD7}%</span>
                  </td>
                  <td className="py-2 px-3 text-right">
                    <span className={s.retentionD30 > 10 ? 'text-green-400' : s.retentionD30 > 5 ? 'text-yellow-400' : 'text-red-400'}>{s.retentionD30}%</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </ChartCard>

      {/* Segment Size Bar Chart */}
      <ChartCard title="Segment Distribution">
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={segmentStats.map((s: any) => ({ name: SEGMENT_LABELS[s.segment] || s.segment, users: s.userCount, d7: s.retentionD7 }))}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
            <XAxis dataKey="name" stroke="#888" fontSize={11} angle={-20} textAnchor="end" height={60} />
            <YAxis yAxisId="left" stroke="#888" fontSize={12} />
            <YAxis yAxisId="right" orientation="right" stroke="#34d399" fontSize={12} unit="%" />
            <Tooltip contentStyle={tooltipStyle} />
            <Legend />
            <Bar yAxisId="left" dataKey="users" fill="#FF9C15" radius={[6, 6, 0, 0]} name="Users" />
            <Bar yAxisId="right" dataKey="d7" fill="#34d399" radius={[6, 6, 0, 0]} name="D7 Retention %" />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      {/* Feature -> Retention Correlation */}
      <ChartCard title="Feature Impact on D7 Retention (Lift)">
        {correlations.length > 0 ? (
          <div className="space-y-3">
            {correlations.map((c: any) => {
              const barWidth = Math.min(c.lift * 30, 100);
              return (
                <div key={c.feature} className="flex items-center gap-3">
                  <div className="w-44 text-xs text-gray-400 text-right truncate">{c.feature}</div>
                  <div className="flex-1 flex items-center gap-2">
                    <div className="flex-1 h-7 bg-white/5 rounded-lg overflow-hidden relative">
                      <div className="h-full rounded-lg transition-all" style={{
                        width: `${barWidth}%`,
                        background: c.lift >= 2 ? 'linear-gradient(to right, #34d399, #22c55e)' : c.lift >= 1.3 ? 'linear-gradient(to right, #FF9C15, #fbbf24)' : 'linear-gradient(to right, #ef4444, #f87171)',
                      }} />
                      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-white font-bold">{c.lift}x lift</span>
                    </div>
                    <div className="w-32 text-[10px] text-gray-500">
                      {c.retD7With}% vs {c.retD7Without}%
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : <p className="text-gray-500 text-sm">No correlation data</p>}
      </ChartCard>

      {/* Top Feature Combinations */}
      <ChartCard title="Best Feature Combinations for Retention">
        {combos.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-gray-400 border-b border-white/10">
                  <th className="text-left py-2 px-3">Features</th>
                  <th className="text-right py-2 px-3">Users</th>
                  <th className="text-right py-2 px-3">Avg Days</th>
                  <th className="text-right py-2 px-3">Avg Sessions</th>
                  <th className="text-right py-2 px-3">D7 Ret</th>
                  <th className="text-right py-2 px-3">D30 Ret</th>
                </tr>
              </thead>
              <tbody>
                {combos.slice(0, 20).map((c: any, i: number) => (
                  <tr key={i} className="border-b border-white/5 hover:bg-white/5">
                    <td className="py-2 px-3 text-white font-medium">{c.features.join(' + ')}</td>
                    <td className="py-2 px-3 text-right text-gray-300">{formatNumber(c.userCount)}</td>
                    <td className="py-2 px-3 text-right text-gray-300">{c.avgDaysActive}</td>
                    <td className="py-2 px-3 text-right text-gray-300">{c.avgSessions}</td>
                    <td className="py-2 px-3 text-right">
                      <span className={`font-bold ${c.retentionD7 > 20 ? 'text-green-400' : c.retentionD7 > 10 ? 'text-yellow-400' : 'text-red-400'}`}>{c.retentionD7}%</span>
                    </td>
                    <td className="py-2 px-3 text-right">
                      <span className={c.retentionD30 > 10 ? 'text-green-400' : 'text-red-400'}>{c.retentionD30}%</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <p className="text-gray-500 text-sm">No combination data</p>}
      </ChartCard>

      {/* Power User Tiers */}
      <ChartCard title="Power User Tiers">
        {powerUsers.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {powerUsers.map((p: any) => (
              <div key={p.tier} className="p-4 bg-white/5 rounded-xl">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-bold text-white">{p.tier}</span>
                  <span className="text-lg font-bold text-[#FF9C15]">{formatNumber(p.count)}</span>
                </div>
                <div className="text-xs text-gray-400 mb-3">
                  Avg {p.avgFeatures} features | {p.avgSessions} sessions | {p.avgDaysActive} days
                </div>
                <div className="space-y-1">
                  {(p.topFeatures || []).map((f: any) => (
                    <div key={f.feature} className="flex items-center justify-between text-xs">
                      <span className="text-gray-300">{f.feature}</span>
                      <div className="flex items-center gap-2">
                        <div className="w-20 h-2 bg-white/10 rounded-full overflow-hidden">
                          <div className="h-full bg-[#FF9C15] rounded-full" style={{ width: `${f.pct}%` }} />
                        </div>
                        <span className="text-gray-400 w-10 text-right">{f.pct}%</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : <p className="text-gray-500 text-sm">No power user data</p>}
      </ChartCard>
    </div>
  );
};

export default UserSegmentsTab;
